import { makeAutoObservable } from 'mobx';
import { decrypt } from '../../utils/helpers/data-processing-helpers';
import { authService } from '../../utils/services/auth-service';
import { v4 as uuidv4 } from 'uuid';
import { localStoragePageInfo } from '../../utils/services/local-storage-service';
import ErrorParsingService, { IErrorObject } from '../../utils/services/error-parsing-service';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import { loginApi, IPresenterLoginResponse } from '../../api/dao/auth-dao';
import MixpanelService from '../../utils/services/mixpanel-service';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';

export interface IUserIdTypes {
  userId?: string;
  knovioUserId?: number;
}

export interface IUserInfo {
  accountId: number;
  accountOrganizationName: string;
  accountPlanId: number;
}

export interface IDecodedJwtToken extends IUserInfo, Partial<IUserIdTypes> {
  presentationId: number;
  iss: string;
  exp: number;
  iat: number;
  userName: string;
}

class AuthStore {
  public isGetPresentationInfoSuccess: boolean;
  public errorMessage: string;
  private podiumUserSessionInfo: IDecodedJwtToken | null;
  private presenterEmbedId: string;
  private errorParsingService: ErrorParsingService;
  public isLoginWithoutAuth: boolean;

  constructor (
    presenterEmbedId: string,
    private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
    private readonly mixpanelService: MixpanelService = getMixpanelService()
  ) {
    makeAutoObservable(this);
    this.isGetPresentationInfoSuccess = false;
    this.errorMessage = '';
    this.podiumUserSessionInfo = authService.decodeJwt(authService.getJwtToken(presenterEmbedId) || '');
    this.presenterEmbedId = presenterEmbedId;
    this.errorParsingService = new ErrorParsingService();
    this.isLoginWithoutAuth = false;
  }

  public presenterLogin = async (presenterLink: string, accessCode: string, id: string | null): Promise<void> => {
    const userIdToSend: IUserIdTypes = id ? { knovioUserId: Number(id) } : { userId: uuidv4() };
    try {
      const result: IPresenterLoginResponse = await loginApi({
        presenterLink: presenterLink,
        accessCode: accessCode,
        ...userIdToSend
      });

      const decodedResult: IDecodedJwtToken | null = authService.decodeJwt<IDecodedJwtToken>(result.token);

      authService.setJwtToken(this.presenterEmbedId, result.token);

      this.podiumUserSessionInfo = decodedResult;

      this.podiumInfoStore.setPresentationId(decodedResult?.presentationId.toString() || '');

      this.isGetPresentationInfoSuccess = true;
      if (decodedResult?.knovioUserId) {
        localStoragePageInfo.setStorageProperty(this.presenterEmbedId, 'knovioUserId', id || '');
      }
      this.mixpanelService.track(
        MIXPANEL_EVENT_IDENTIFIERS.SIGN_IN,
        MIXPANEL_EVENT_TYPES.CLICKED
      );

    } catch (e) {
      const error = e as IErrorObject;
      this.isGetPresentationInfoSuccess = false;
      this.errorMessage = this.errorParsingService.parseApiErrorMessage(error);
    }
  };

  public getPodiumUserSessionInfo = (): IDecodedJwtToken | null => {
    return this.podiumUserSessionInfo;
  };

  private loginWithoutAuth = async (id: string, presenterLink: string, urlHash: string): Promise<void> => {
    const numberRemovedElements = 1;
    const [accessCode, userId] = decrypt(id, urlHash.slice(numberRemovedElements)).split('/');
    await this.presenterLogin(presenterLink, accessCode, userId);
    if (this.isGetPresentationInfoSuccess) {
      this.isLoginWithoutAuth = true;
    }
  };

  private loginWithoutAuthCall = async (id: string, presenterLink: string, urlHash: string): Promise<void> => {
    if (!urlHash) return Promise.resolve();
    await this.loginWithoutAuth(id, presenterLink, urlHash);
  };

  public autoLoginUserCall = async (id: string, presenterLink: string, urlHash: string, errorCallback: () => void): Promise<void> => {
    try {
      await Promise.all([getPodiumInfoStore().getPresentationInfo(id), this.loginWithoutAuthCall(id, presenterLink, urlHash)]);
    }
    catch (e) {
      errorCallback();
    }
  };

  public getPresenterUserId = (): string | null => {
    return localStoragePageInfo.getStorageProperty(this.presenterEmbedId, 'knovioUserId') ||
      localStoragePageInfo.getStorageProperty(this.presenterEmbedId, 'userId') || null;
  };
}

export type TAuthStore = typeof AuthStore;

export default AuthStore;
